import {
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    Injectable,
    Injector,
    ReflectiveInjector,
    ResolvedReflectiveProvider
} from "@angular/core";
import {ResponsiveService} from "./responsive.service";
import {Subject} from "rxjs";
import {Type} from "@angular/core/src/type";
import {Page, PageGeometry, PageSpec} from "./page";
import {OrchestrationEvents} from "./orchestration-events";
import {View} from "./view";
import {NavigationParams} from "./navigation-params";
import {NotificationService} from "../notification/notification.service";
import {OrderModel} from "../order/order.model";

class ViewHistory {
    constructor(public index: number, public previousHistoryIndex: number) {
    }
}

function isViewHistory(state: any): state is ViewHistory {
    return state && (state as ViewHistory).index !== undefined;
}

/**
 * Manages navigation including full-pages, modals, views, and back-button handling.
 */
@Injectable()
export class NavigationService {
    public pageCreated$: Subject<PageSpec> = new Subject<PageSpec>();
    public pageDismissed$: Subject<ComponentRef<any>> = new Subject<ComponentRef<any>>();
    public viewRegistered: Subject<View> = new Subject<View>();
    public viewDeregistered: Subject<View> = new Subject<View>();

    private componentFactories: { [selector: string]: ComponentFactory<any> } = {};
    private views: View[] = [];
    private previousHistoryStateIndex: number;
    private historyStateInitialisationStarted: boolean = false;
    private historyStateInitialised: boolean = false;
    private pendingPushedStates: number = 0;

    // Avoid injection dependency on NotificationService and OrderModel
    private _notificationService: NotificationService;
    private _orderModel: OrderModel;

    constructor(private injector: Injector,
                private responsiveService: ResponsiveService,
                private componentFactoryResolver: ComponentFactoryResolver) {
        console.log("--- NavigationService");

        // Listen for browser back button - TODO - replace for Service App
        // platform.registerBackButtonAction(() => this.dismissView());

        if (!this.responsiveService.isApp) {

            // history.pushstate generally only works in response to a user gesture, so there's no point in
            // pushing our initial state until we see a user event.
            // Using the browser back button prior to that will exit the app, and there's nothing we can do about it.
            window.addEventListener("touchend", () => this.initialiseHistoryState());
            window.addEventListener("mouseup", () => this.initialiseHistoryState());
            window.addEventListener("popstate", () => this.historyStatePopped());
        }
    }

    public set notificationService(notificationService: NotificationService) {
        this._notificationService = notificationService;
    }

    public set orderModel(orderModel: OrderModel) {
        this._orderModel = orderModel;
    }

    private get activeView(): View {
        return this.views.length ? this.views[this.views.length - 1] : undefined;
    }

    private get activeViewName(): string {
        return this.views.length ? this.views[this.views.length - 1].viewName : undefined;
    }

    public pageIsActive(page: Page): boolean {
        return this.activePage === page;
    }

    private get activePage(): Page {
        if (!this.views.length) {
            return undefined;
        }
        for (let i: number = this.views.length - 1; i >= 0; i--) {
            const view: View = this.views[i];
            if (view instanceof Page) {
                return view;
            }
        }
        return undefined;
    }

    private get historyStateDetails(): string {
        return !this.historyStateInitialised || history.state == null ? null : isViewHistory(history.state) ? `{index: ${history.state.index}}` : history.state;
    }

    private get historyStateIndex(): number {
        return !this.historyStateInitialised || history.state == null ? null : isViewHistory(history.state) ? history.state.index : -1;
    }

    private traceHistoryState(action: string): void {
        console.log(`++++ ${action}: current state = ${this.historyStateDetails}, view stack size = ${this.views.length}, history size = ${history.length}, pending pushed states = ${this.pendingPushedStates}, current view = ${this.activeViewName}`);
    }

    private initialiseHistoryState(): void {
        if (!this.historyStateInitialisationStarted) {
            this.historyStateInitialisationStarted = true;
            this.traceHistoryState("initialising history state");

            // Keep track of history entries from a previous load of the app
            this.previousHistoryStateIndex = isViewHistory(history.state) ? (history.state.index + (history.state.previousHistoryIndex || 0)) : 0;

            // Push a root state (index = 0)
            history.go(-history.length);
            history.replaceState(new ViewHistory(0, this.previousHistoryStateIndex), "");
            this.historyStateInitialised = true;

            // Push states for any views that were registered prior to user interaction
            while (this.pendingPushedStates > 0) {
                this.pendingPushedStates--;
                this.pushHistoryState();
            }
        }
    }

    private pushHistoryState(): void {
        this.traceHistoryState("before pushing history state");

        // We can't push new history states if the history state has not been initialised
        if (!this.historyStateInitialised) {
            this.pendingPushedStates++;
        } else {
            history.pushState(new ViewHistory(this.historyStateIndex + 1, this.previousHistoryStateIndex), "");
        }
        this.traceHistoryState("after pushing history state");
    }

    private popHistoryState(nrStates: number = 1): void {
        if (nrStates <= 0) {
            return;
        }

        this.traceHistoryState(`popping ${nrStates} history state(s)`);

        // If we have pushed states still pending initialisation, we only decrement them before modifying the history.
        const pendingReduction: number = Math.min(this.pendingPushedStates, nrStates);
        if (pendingReduction) {
            this.pendingPushedStates -= pendingReduction;
            nrStates -= pendingReduction;
        }

        if (nrStates > 0) {
            history.go(-nrStates);
        }
    }

    /**
     * Attempts to detect when a browser navigation mechanism has been activated, and to take the appropriate action within the app.
     */
    private async historyStatePopped(): Promise<any> {
        this.traceHistoryState("history state popped");

        // If the state index matches the view stack size, the app has initiated the state "pop" by calling history.back() after deregistering a view.
        // No further action is needed.
        if (this.historyStateIndex === this.views.length) {
            return;
        }

        // If the state index exceeds the view stack size, this is an attempted forward navigation via the browser.
        // Reverse it.
        if (this.historyStateIndex > this.views.length) {
            this.traceHistoryState("before forward attempt");
            history.go(this.views.length - this.historyStateIndex);
            this.traceHistoryState("after forward attempt");
            return;
        }

        // If the state index is less than the view stack size, the user has navigated back manually (using a browser navigation such as a back button).
        // If we have nothing to go back to, confirm whether the user wants to exit the app
        if (this.views.length == 1) {

            // This confirmation popup is displayed without pushing a history state (or popping one when dismissed).
            // This is because we are not opening it in response to a user interaction with the web page, and as such,
            // the history state would not trigger a popstate event.
            if (await this._notificationService.displayConfirmation("woa.heading.exit-app", undefined, this._orderModel.hasOrderedItems ? "woa.message.exit-app" : undefined,
                "woa.button.common.noThanks", "woa.button.exit-app",
                true, undefined, false, undefined, false)) {

                // exit the app or reload
                if (history.length - this.previousHistoryStateIndex > 3) {
                    this.traceHistoryState("before exit");
                    this.views = [];
                    history.go(-history.length);
                    history.back();
                } else {
                    window.location.reload();
                }
            } else {

                // If the user declines to exit, re-push the history state that was popped now that we are working in the context of a user interaction.
                this.pushHistoryState();
            }
            return;
        }

        // Dismiss the active view if we have one.
        while (this.views.length > 1 && this.historyStateIndex < this.views.length) {
            await this.dismissView(false);
            this.traceHistoryState("after view dismissal");
        }
    }

    public registerComponents(componentTypes: Type<any>[]): void {
        componentTypes.forEach((componentType: Type<any>) => {
            const componentFactory: ComponentFactory<any> = this.componentFactoryResolver.resolveComponentFactory(componentType);
            this.componentFactories[componentFactory.selector] = componentFactory;
        });
    }

    public getComponentFactory(selector: string): ComponentFactory<any> {
        const componentFactory: ComponentFactory<Page> = this.componentFactories[selector];
        if (!componentFactory) {
            console.log("No component factory found for selector '" + selector + "'. Check that it has been added to entryComponents.");
            return undefined;
        }
        return componentFactory;
    }

    public redirectToUrl(url: string): void {
        OrchestrationEvents.redirecting$.next(true);
        window.location.href = url;
    }

    /**
     * Creates a logical view within the current page.
     * The promise resolves when the view is dismissed, but will immediately resolve as false if the view already exists on the specified parent page.
     * @param viewName The logical view name
     * @param parentPage The page creating the view
     */
    public async createView(viewName: string, parentPage: Page): Promise<boolean> {

        // Detect same view on specified page
        if (this.views.some((view: View) => view.viewName === viewName && view.parentPage === parentPage)) {
            return false;
        }

        const view: View = new View();
        view.viewName = viewName;
        view.parentPage = parentPage;
        this.registerView(view);

        // Promise is resolved on view dismissal
        await view.dismissal;
        return true;
    }

    public async createPage(selector, params?: any, geometry?: PageGeometry, asRoot: boolean = false, forceIosFocus: boolean = false, pushHistory: boolean = true): Promise<any> {

        // Destroy all pages if setting a new root - normal dismissal lifecycle and transitions do not apply
        let viewsDeregistered: number = 0;
        if (asRoot) {
            while (this.views.length) {
                const activeView: View = this.activeView;
                if (activeView instanceof Page) {
                    activeView.componentRef.destroy();
                    activeView.containerComponentRef.destroy();
                }
                this.deregisterView(activeView, false);
                viewsDeregistered++;
            }
        }

        // Create the page component
        const providers: ResolvedReflectiveProvider[] = ReflectiveInjector.resolve([{
            provide: NavigationParams,
            useValue: new NavigationParams(params)
        }]);
        const injector: ReflectiveInjector = ReflectiveInjector.fromResolvedProviders(providers, this.injector);
        const componentFactory: ComponentFactory<Page> = this.getComponentFactory(selector);
        if (!componentFactory) {
            return undefined;
        }
        const componentRef: ComponentRef<Page> = componentFactory.create(injector);
        const page: Page = componentRef.instance;
        page.viewName = page.constructor.name;
        page.componentRef = componentRef;

        const activePage: Page = this.activePage;
        if (activePage) {
            activePage.onInactive();
        }

        // Register the page component as a view
        this.registerView(page, pushHistory);

        // When setting a new root, pop the history state for deregistered views after pushing the state for the new page.
        // This is because the position in the history that history.go jumps to is relative to the position at the time the
        // call is made, even if other states are pushed before the new position is actually applied.
        // I.e. we end up with the history position one state too far back, which looks like a back-button press.
        if (viewsDeregistered) {
            this.popHistoryState(viewsDeregistered);
        }

        // Workaround for iOS+Safari behaviour where the keyboard doesn't open on setting focus automatically to a field in the page,
        // since it does not recognise that user interaction took place before the open transition.
        let input: HTMLInputElement;
        if (forceIosFocus && this.responsiveService.isIos) {
            input = document.createElement("input");
            input.style.width = "0";
            input.style.height = "0";
            input.style.margin = "0";
            input.style.padding = "0";
            input.style.border = "0";
            input.style.opacity = "0";
            document.body.appendChild(input);
            input.focus();
        }

        // Display the view
        this.pageCreated$.next(new PageSpec(componentRef, geometry));

        componentRef.instance.onActive();

        // Promise is resolved on page dismissal
        const result: any = await page.dismissal;
        if (input) {
            document.body.removeChild(input);
        }
        return result;
    }

    public canDismissView(): boolean {
        return this.views.length > 1;
    }

    public async dismissView(popHistory: boolean = true): Promise<any> {
        const activeView: View = this.activeView;
        if (activeView instanceof Page) {
            return this.dismissPage(activeView, undefined, false, popHistory);
        } else {
            this.deregisterView(activeView, popHistory);
            activeView.viewPromiseResolve();
        }
    }

    public async dismissPage(page: Page, results?: any, failure: boolean = false, popHistory: boolean = true): Promise<boolean> {
        for (let i: number = this.views.length - 1; i >= 0; i--) {
            if (this.views[i] === page) {

                // Allow the page component to prevent the dismissal (asynchronously)
                if (!await page.onWillDismissPage(results)) {
                    return false;
                }

                // Remove the page from the navigation stack along with any successive non-page views
                const wasActive: boolean = this.activePage === page;
                while (i + 1 < this.views.length && !(this.views[i + 1] instanceof Page)) {
                    this.deregisterView(this.views[i + 1], popHistory);
                    popHistory = true;
                }
                this.deregisterView(page, popHistory);
                page.onInactive();

                // If the component we removed was active, notify the new active component
                if (wasActive) {
                    const activePage: Page = this.activePage;
                    if (activePage) {
                        activePage.onActive();
                    }
                }

                // Initiate destruction of the component.
                this.pageDismissed$.next(page.componentRef);

                // The page container destroys the component after it completes the close transition
                page.componentRef.onDestroy(() => {

                    // Notify the component instance that it has been dismissed
                    page.onDidDismissPage(results);

                    // Resolve or reject the promise returned from createPage
                    if (failure) {
                        page.viewPromiseReject(results);
                    } else {
                        page.viewPromiseResolve(results);
                    }
                });

                return true;
            }
        }

        return false;
    }

    private registerView(view: View, pushHistory: boolean = true): void {
        console.log("Registering view: " + view.viewName);
        this.views.push(view);
        if (pushHistory) {
            this.pushHistoryState();
        }
        this.viewRegistered.next(view);
    }

    private deregisterView(view: View, popHistory: boolean = true): void {
        console.log("Deregistering view: " + view.viewName);
        let index = this.views.indexOf(view);
        if (index != -1) {
            this.views.splice(index, 1);
            if (popHistory) {
                this.popHistoryState(1);
            }
        }
        this.viewDeregistered.next(view);
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/navigation.service.ts